import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import ctaBg from '../landing/assets/about_us_4.webp';
import iconApple from '../landing/assets/Apple.png';
import iconGoogle from '../landing/assets/Playstore.png';
import badminton from '../landing/assets/sports/badminton.jpg';
import padel from '../landing/assets/sports/padel.jpg';
import pickleball from '../landing/assets/sports/pickleball.jpg';
import squash from '../landing/assets/sports/squash.jpg';
import tennis from '../landing/assets/sports/tennis.jpg';
import infographic from '../landing/assets/infographicv2.jpg';
import sportsLeagueFeaturesBg from '../landing/assets/sports/League2.png';
import sportsLeagueHeroBg from '../landing/assets/sports_league_hero_bg.webp';
import sportsLeagueNextLevelBg from '../landing/assets/sports_league_next_level_bg.webp';
import sportsLeagueWhyBg from '../landing/assets/sports_league_why_bg.webp';
import sportsLeagueIcon1 from '../landing/assets/svgs/scheduling_icon.svg';
import sportsLeagueIcon2 from '../landing/assets/svgs/sports_league_icon_2.svg';
import sportsLeagueIcon3 from '../landing/assets/svgs/sports_league_icon_3.svg';
import sportsLeagueIcon4 from '../landing/assets/svgs/sports_league_icon_4.svg';
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

const SPORTS = [
    {title: "Padel", backgroundImage: padel, href: "/padel-tournament-software/"},
    {title: "Tennis", backgroundImage: tennis, href: "/tennis-tournament-software/"},
    {title: "Pickleball", backgroundImage: pickleball, href: "/pickleball-tournament-software/"},
    {title: "Badminton", backgroundImage: badminton, href: "/badminton-tournament-software/"},
    {title: "Squash", backgroundImage: squash, href: "/squash-tournament-software/"},
];

const FAQs = [
    {
        q: "What is sports league software, and how does ScorePal help?",
        a: <>Sports league software is a system designed to simplify scheduling, registrations, standings, scoring, and communication. Our sports league management software provides all the tools needed to manage leagues efficiently, helping organizers save time, reduce manual work, and improve overall operations.</>,
    },
    {
        q: "Can ScorePal manage youth sports leagues and multiple divisions?",
        a: <>Yes, our team supports youth leagues, adult competitions, and multi-division formats. This sports league software allows administrators to manage teams, players, coaches, and seasons from a centralized dashboard, making it ideal for complex league structures.
           </>,
    },
    {
        q: "Does ScorePal support online registration and payments?",
        a: <>Yes, ScorePal includes built-in sports league management software features for secure online registrations and payments. Teams, players, and parents can sign up easily, while organizers can manage forms, fees, and data in one place using our sports registration and scheduling software.</>,
    },
    {
        q: "How do live scoring and sports analytics work?",
        a: <>ScorePal’s sports league software offers real-time scoring and analytics tools. As match results are entered, standings, rankings, and performance stats update instantly, giving organizers and participants accurate insights across both league and tournament formats using our tournament and league management software.</>,
    },
    {
        q: "Is ScorePal suitable for growing leagues and sports organizations?",
        a: <>Yes, ScorePal is designed to scale with your needs. This tournament and league management software supports clubs and organizations of all sizes, helping them manage growth, streamline operations, and even generate revenue through features like sponsorships, events, and merchandise.</>,
    },
    {
        q: "How does sports league management software work?",
        a: <>Sports league management software automates and simplifies the entire league lifecycle, from league creation and scheduling to registrations, scoring, standings, and communication. ScorePal’s sports league management software centralizes all operations in one platform, allowing organizers to manage teams, track results, and communicate with participants efficiently without relying on spreadsheets or manual processes.</>,
    },
    {
        q: "Can I manage both leagues and tournaments using ScorePal?",
        a: <>Yes, ScorePal is a complete tournament and league management software that allows you to manage both leagues and tournaments in one place. You can create different formats, schedule matches, track scores, and manage participants seamlessly using our sports league software, making it ideal for clubs and organizers handling multiple competitions.</>,
    },
];

export const Route = createFileRoute('/sports-league-management-software')({
    component: RouteComponent,
})

function RouteComponent() {
    const features = [
        {
            icon:        sportsLeagueIcon4,
            alt:         'scheduling icon',
            title:       'League Creation & Setup',
            description: 'Easily create and manage leagues of any size with ScorePal’s sports league software. Set up divisions, teams, formats, and rules in just a few clicks, perfect for clubs and organizers looking for efficient league creation.',
        },
           {
            icon:        sportsLeagueIcon3,
            alt:         'scheduling icon',
            title:       'Automated Scheduling',
            description: (
        <>
            Build conflict-free schedules in minutes using advanced sports
            registration and scheduling software. ScorePal automatically generate
            fixtures, schedules, and brackets using intelligent tournament brackets software, ensuring smooth match flow and organization.

                <a
                    href="https://scorepal.co/create-brackets/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#082F8A] underline font-semibold"
                >
                tournament brackets software
                </a>

            </>
                ),
            },
        {
            icon:        sportsLeagueIcon1,
            alt:         'scheduling icon',
            title:       'Easy Online Registrations',
            description: 'Simplify sign-ups with integrated sports registration and scheduling software that allows players, teams, and officials to register online. Collect payments, forms, and waivers securely, all in one place.',
        },
        {
            icon:        sportsLeagueIcon2,
            alt:         'sports league',
            title:       'Real-Time Standings & Stats',
            description: 'Track scores, rankings, and performance with powerful sports league software tools. Standings and statistics update instantly as results are entered, ensuring accuracy and transparency across your league.',
        },
        {
            icon:        sportsLeagueIcon3,
            alt:         'sports league',
            title:       'Team Management Tools',
            description: 'Organize players, coaches, and teams effortlessly with built-in sports team management software features. Manage rosters, roles, and participation from a centralized dashboard.',
        },
        {
            icon:        sportsLeagueIcon4,
            alt:         'sports league',
            title:       'Built-In Communication Tools',
            description: 'Keep everyone informed with integrated communication features. Send announcements, match updates, and reminders via email, SMS, and app notifications- making ScorePal a complete sports league software solution for seamless communication.',
        },
    ];

    const whyChooseItems = [
        'Reduce administrative workload with automated workflows',
        'Improve accuracy and organization with centralized data management',
        'Enhance participant engagement with real-time updates and notifications',
        'Manage everything from one dashboard using advanced sports team management software tools',
        'Access real-time scores, standings, and performance statistics',
        'Simplify operations with all-in-one sports registration and scheduling software',
        'Scale effortlessly as your league or club grows',
        'Deliver a professional experience for players, coaches, and organizers',
        'Automate scheduling, scoring, and communication to save time',
        'Support multiple leagues, divisions, and tournaments with ease',
    ];

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context":   "https://schema.org",
                "@type":      "FAQPage",
                "mainEntity": [
                    {
                        "@type":          "Question",
                        "name":           "What is sports league software, and how does ScorePal help?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Sports league software is a system designed to simplify scheduling, registrations, standings, scoring, and communication. Our sports league management software provides all the tools needed to manage leagues efficiently, helping organizers save time, reduce manual work, and improve overall operations."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Can ScorePal manage youth sports leagues and multiple divisions?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes, our team supports youth leagues, adult competitions, and multi-division formats. This sports league software allows administrators to manage teams, players, coaches, and seasons from a centralized dashboard, making it ideal for complex league structures."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Does ScorePal support online registration and payments?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes, ScorePal includes built-in sports league management software features for secure online registrations and payments. Teams, players, and parents can sign up easily, while organizers can manage forms, fees, and data in one place using our sports registration and scheduling software."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "How do live scoring and sports analytics work?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "ScorePal’s sports league software offers real-time scoring and analytics tools. As match results are entered, standings, rankings, and performance stats update instantly, giving organizers and participants accurate insights across both league and tournament formats using our tournament and league management software."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "How does ScorePal improve team and parent communication?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "ScorePal includes communication tools that allow administrators to send messages, schedules, reminders, and updates through email, SMS, and app notifications, keeping teams, coaches, and parents informed throughout the season."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Is ScorePal suitable for growing leagues and sports organizations?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes, ScorePal is designed to scale with your needs. This tournament and league management software supports clubs and organizations of all sizes, helping them manage growth, streamline operations, and even generate revenue through features like sponsorships, events, and merchandise."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "How does sports league management software work?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Sports league management software automates and simplifies the entire league lifecycle, from league creation and scheduling to registrations, scoring, standings, and communication. ScorePal’s sports league management software centralizes all operations in one platform, allowing organizers to manage teams, track results, and communicate with participants efficiently without relying on spreadsheets or manual processes."
                        }
                    },
                    {
                        "@type":          "Question",
                        "name":           "Can I manage both leagues and tournaments using ScorePal?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text":  "Yes, ScorePal is a complete tournament and league management software that allows you to manage both leagues and tournaments in one place. You can create different formats, schedule matches, track scores, and manage participants seamlessly using our sports league software, making it ideal for clubs and organizers handling multiple competitions."
                        }
                    }
                ]
            }} />
            <JsonLd id={`page${Route.id}/app`} item={{
                "@context":            "https://schema.org",
                "@type":               "SoftwareApplication",
                "name":                "Sports League Management Software",
                "url":                 "https://scorepal.co/sports-league-management-software/",
                "applicationCategory": "SportsApplication",
                "operatingSystem":     ["Web", "Android", "iOS"],
                "description":         "Manage sports leagues with automated fixtures, standings, team tracking, and scheduling tools designed for clubs and organizers. Proven performance over the past 2 years.",
                "offers":              {
                    "@type":         "Offer",
                    "price":         "0",
                    "priceCurrency": "USD"
                }
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Sports League Management Software", item: "https://scorepal.co/sports-league-management-software/"}]} />
            <title>Sports League Management Software | Live Scoring & Standings | ScorePal</title>
            <meta name="description" content="Sports league management software for clubs with live scoring, standings, league software tools, and scheduling. Manage leagues effortlessly." />
            <link rel="canonical" href={`${config.publicUrl}/sports-league-management-software/`} />

            <section className="relative overflow-hidden p-4">
                <div
                    className="flex flex-col min-h-screen justify-between relative pb-16 rounded-4xl bg-[#EDEEF4] bg-top bg-cover bg-no-repeat saturate-[0.87]"
                    style={{
                        backgroundImage: `url(${sportsLeagueHeroBg})`
                    }}
                >
                    <div className="absolute inset-0 bg-linear-to-r from-black/60 via-black/30 to-transparent rounded-4xl" />

                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8 flex-1 flex flex-col justify-center relative z-10">
                        <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-4">
                            SPORTS LEAGUE MANAGEMENT <br />SOFTWARE FOR CLUBS & ORGANIZERS
                        </h1>

                        <p className="font-inter text-white/90 text-base lg:text-xl mb-8 max-w-2xl leading-relaxed">
                            Manage sports leagues with ease using ScorePal’s all-in-one system for fixtures, standings, live scoring, and automated competition tracking.
                        </p>

                        <Link
                            to={"/signup/player/" as any}
                            className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                        >
                            <span className="font-inter text-base font-medium">Lets Get Started</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>

                        <div className="flex flex-wrap gap-4 mt-16 lg:mt-60">
                            <a
                                href="https://play.google.com/store/apps/details?id=co.scorepal.mobile&pcampaignid=web_share"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block"
                            >
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-100 transition">
                                    <img src={iconGoogle} alt="Google Play" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">GET IT ON</div>
                                        <div className="text-white font-semibold text-sm">Google Play</div>
                                    </div>
                                </div>
                            </a>

                            <a
                                href="https://apps.apple.com/us/app/score-pal/id6745509167"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block"
                            >
                                <div className="bg-white/10 rounded-xl px-6 py-3 flex items-center gap-3 hover:bg-gray-900 transition">
                                    <img src={iconApple} alt="App Store" className="h-8" />
                                    <div className="text-left">
                                        <div className="text-white/70 text-xs">Download on the</div>
                                        <div className="text-white font-semibold text-sm">App Store</div>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="flex flex-col gap-8 lg:gap-16">
                        <div className="text-center">
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                                <span className="text-[#082F8A]">STREAMLINE</span> YOUR LEAGUES WITH POWERFUL,AUTOMATED MANAGEMENT
                            </h2>

                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                                      Running a sports league shouldn't be complicated. With ScorePal's online sports league management software, you get an all-in-one platform designed to simplify every part of league operations, especially when dealing with manual scheduling, registration chaos, spreadsheets, and score tracking errors. Whether you manage youth leagues, adult competitions, or multi-sport organizations, ScorePal helps you save time, reduce admin work, and deliver a better experience for teams and participants using advanced{" "}
  
                              <a
                                    href="/tournament-management-software/"
                                    className="text-[#082F8A] underline font-semibold"
                                          >
                                    tournament management software
                              </a>
                                  .
                                </p>
                        </div>

                        <div>
                            <img
                                src={sportsLeagueNextLevelBg}
                                alt="Sports league management"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                        </div>
                    </div>
                </section>

                 <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                    <div className="container mx-auto px-6 lg:px-12">

                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8 text-center">
                            Key Features of <span className="text-[#082F8A]">ScorePal</span> Sports League Software
                        </h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-12">
                        {features.map((feature, index) => (
                        <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-[#EDEEF4] flex items-center justify-center shrink-0">
                            <img src={feature.icon} alt="" className="w-6 h-6" />
                        </div>

                        <h3 className="font-inter text-[#082F8A] font-semibold text-base">
                            {feature.title}
                        </h3>
                    </div>

                    <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">
                        {feature.description}
                    </p>
                </div>
            ))}
        </div>
    </div>
</section>

            <div className="flex justify-center">
                <Link
                    to={"/signup/player/" as any}
                    className="flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 w-fit font-semibold hover:bg-white transition"
                >
                    <span className="font-inter text-base font-medium">Lets Get Started</span>
                    <div className="bg-[#082F8A] rounded-full p-1">
                        <ArrowUpRightIcon className="text-white" size={28} />
                    </div>
                </Link>
            </div>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-8">
                                Why Choose ScorePal <br />Sports League Software?
                            </h2>

                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed mb-8">
                            ScorePal is a powerful sports league management software for clubs that combines automation, organization, and engagement tools to help you run leagues more efficiently. Whether you're managing a local club or a large competition, our sports league software simplifies every step of the process and supports both league and tournament operations in one platform.
                            </p>

                            <ul className="space-y-4">
                                {whyChooseItems.map((item, index) => (
                                    <li key={index} className="flex items-center gap-4">
                                        <CheckBadgeIcon size={24} />
                                        <span className="font-inter text-[#1a1a2e] text-base">
                                            {item}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <img
                                src={sportsLeagueFeaturesBg}
                                alt="Why choose ScorePal"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        <div className="hidden lg:block">
                            <img
                                src={sportsLeagueWhyBg}
                                alt="Take your league to the next level"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>

                        <div>
                            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                                TAKE YOUR LEAGUE MANAGEMENT<br />TO THE NEXT LEVEL
                            </h2>

                            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                                ScorePal gives you the tools to run smoother, smarter, and more professional leagues. Automate the busywork and focus on what matters—growing your programs and creating unforgettable experiences for your athletes.
                            </p>
                        </div>

                        <div className="block lg:hidden">
                            <img
                                src={sportsLeagueWhyBg}
                                alt="Take your league to the next level"
                                className="w-full h-auto rounded-3xl object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-8 lg:py-16">
                <div className="container mx-auto px-6 lg:px-12">
                    <div
                        className="rounded-3xl py-16 lg:py-24 px-8 text-center bg-top bg-cover bg-no-repeat"
                        style={{backgroundImage: `url(${ctaBg})`}}
                    >
                        <h2 className="font-bebas text-white uppercase text-[clamp(28px,4vw,48px)] leading-tight font-bold tracking-wide mb-4">
                            See How ScorePal Simplifies League Management
                        </h2>
                        <p className="font-inter text-white/90 text-base lg:text-lg mb-8 max-w-3xl mx-auto leading-relaxed">
                            Get a quick look at how ScorePal streamlines scheduling, registrations, scoring, and communication, all in one platform designed for modern sports organizations.
                            <br /><br />
                            Best sports league management software for clubs & organizers
                        </p>
                        <Link
                            to={"/signup/player/" as any}
                            className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition"
                        >
                            <span className="font-inter text-base font-medium">Lets Get Started</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                    
                </div>
            </section>
            <section className="py-16 lg:py-24 bg-white">
            <div className="container mx-auto px-6 lg:px-12">

            <div className="text-center max-w-5xl mx-auto mb-12">
            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                Manual vs Sports League Management Software
            </h2>

            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                Managing leagues manually can be time-consuming and error-prone.
                Here’s how ScorePal’s sports league management software compares
                to traditional methods.
            </p>
        </div>

        <div className="overflow-x-auto">
            <div className="min-w-[800px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">

                {/* Table Header */}
                <div className="grid grid-cols-2 bg-[#F6F7FB]">
                    <div className="p-6 border-r border-[#d9dce7]">
                        <h3 className="font-bebas text-2xl text-[#1a1a2e] text-center">
                            Manual Management
                            <br />
                            <span className="text-[#082F8A]">
                                (Spreadsheets & Tools)
                            </span>
                        </h3>
                    </div>

                    <div className="p-6">
                        <h3 className="font-bebas text-2xl text-[#082F8A] text-center">
                            ScorePal Sports League Software
                        </h3>
                    </div>
                </div>

                {/* Row 1 */}
                <div className="grid grid-cols-2 border-t border-[#d9dce7]">
                    <div className="p-5 border-r border-[#d9dce7] font-inter text-[#1a1a2e]/80">
                        Manual scheduling with high risk of conflicts
                    </div>

                    <div className="p-5 font-inter text-[#1a1a2e] font-medium">
                        Automated scheduling system with conflict-free fixtures
                    </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-2 border-t border-[#d9dce7] bg-[#FAFBFD]">
                    <div className="p-5 border-r border-[#d9dce7] font-inter text-[#1a1a2e]/80">
                        Scattered spreadsheets and data silos
                    </div>

                    <div className="p-5 font-inter text-[#1a1a2e] font-medium">
                        Centralized dashboard for all league data
                    </div>
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-2 border-t border-[#d9dce7]">
                    <div className="p-5 border-r border-[#d9dce7] font-inter text-[#1a1a2e]/80">
                        Time-consuming registrations and paperwork
                    </div>

                    <div className="p-5 font-inter text-[#1a1a2e] font-medium">
                        Seamless online registrations with payments
                    </div>
                </div>

                {/* Row 4 */}
                <div className="grid grid-cols-2 border-t border-[#d9dce7] bg-[#FAFBFD]">
                    <div className="p-5 border-r border-[#d9dce7] font-inter text-[#1a1a2e]/80">
                        Delayed score updates and manual entry errors
                    </div>

                    <div className="p-5 font-inter text-[#1a1a2e] font-medium">
                        Real-time scoring with instant updates
                    </div>
                </div>

                {/* Row 5 */}
                <div className="grid grid-cols-2 border-t border-[#d9dce7]">
                    <div className="p-5 border-r border-[#d9dce7] font-inter text-[#1a1a2e]/80">
                        Limited communication across teams
                    </div>

                    <div className="p-5 font-inter text-[#1a1a2e] font-medium">
                        Built-in communication tools (SMS, email, app)
                    </div>
                </div>

                {/* Row 6 */}
                <div className="grid grid-cols-2 border-t border-[#d9dce7] bg-[#FAFBFD]">
                    <div className="p-5 border-r border-[#d9dce7] font-inter text-[#1a1a2e]/80">
                        Difficult to manage multiple leagues or tournaments
                    </div>

                    <div className="p-5 font-inter text-[#1a1a2e] font-medium">
                        Scalable tournament and league management software
                    </div>
                </div>

                {/* Row 7 */}
                <div className="grid grid-cols-2 border-t border-[#d9dce7]">
                    <div className="p-5 border-r border-[#d9dce7] font-inter text-[#1a1a2e]/80">
                        High administrative workload
                    </div>

                    <div className="p-5 font-inter text-[#1a1a2e] font-medium">
                        Automated workflows that save time
                    </div>
                </div>

                {/* Row 8 */}
                <div className="grid grid-cols-2 border-t border-[#d9dce7] bg-[#FAFBFD]">
                    <div className="p-5 border-r border-[#d9dce7] font-inter text-[#1a1a2e]/80">
                        Lack of transparency for players and parents
                    </div>

                    <div className="p-5 font-inter text-[#1a1a2e] font-medium">
                        Live standings, stats, and performance tracking
                    </div>
                </div>
            </div>
        </div>

        <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed text-center max-w-5xl mx-auto mt-10">
            Unlike spreadsheets, modern tools like league management software and automation platforms eliminate manual errors and improve efficiency.
            
            <a
                href="/tournament-management-software/"
                className="text-[#082F8A] underline font-semibold"
            >
                tournament management software
            </a>
        </p>

        </div>
        </section>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
    <div className="container mx-auto px-6 lg:px-12">

        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto mb-14">
            <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-6">
                How <span className="text-[#082F8A]">ScorePal</span> Sports League Management Software Works
            </h2>

            <p className="font-inter text-[#1a1a2e]/80 text-base leading-relaxed">
                Managing your league is simple with ScorePal’s sports league
                management software. Follow these easy steps to run leagues and
                tournaments efficiently.
            </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

            {/* Step 1 */}
            <div className="bg-white rounded-3xl p-8 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-[#082F8A] text-white flex items-center justify-center font-bold text-lg mb-6">
                    1
                </div>

                <h3 className="font-bebas text-2xl text-[#082F8A] mb-4">
                    Create Your League
                </h3>

                <p className="font-inter text-[#1a1a2e]/80 leading-relaxed">
                    Set up your league structure, divisions, formats, and rules
                    using our intuitive sports league software.
                </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-3xl p-8 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-[#082F8A] text-white flex items-center justify-center font-bold text-lg mb-6">
                    2
                </div>

                <h3 className="font-bebas text-2xl text-[#082F8A] mb-4">
                    Register Teams & Players
                </h3>

                <p className="font-inter text-[#1a1a2e]/80 leading-relaxed">
                    Collect registrations, forms, and payments seamlessly with
                    built-in sports registration and scheduling software.
                </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-3xl p-8 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-[#082F8A] text-white flex items-center justify-center font-bold text-lg mb-6">
                    3
                </div>

                <h3 className="font-bebas text-2xl text-[#082F8A] mb-4">
                    Schedule Matches
                </h3>

                <p className="font-inter text-[#1a1a2e]/80 leading-relaxed">
                    Access detailed insights through a powerful sports analytics
                    dashboard, helping you track performance, analyze trends,
                    and make data-driven decisions.
                </p>
            </div>

            {/* Step 4 */}
            <div className="bg-white rounded-3xl p-8 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-[#082F8A] text-white flex items-center justify-center font-bold text-lg mb-6">
                    4
                </div>

                <h3 className="font-bebas text-2xl text-[#082F8A] mb-4">
                    Manage Standings
                </h3>

                <p className="font-inter text-[#1a1a2e]/80 leading-relaxed">
                    Track rankings, points, and team performance in real time
                    with centralized league management tools.
                </p>
            </div>

            {/* Step 5 */}
            <div className="bg-white rounded-3xl p-8 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-[#082F8A] text-white flex items-center justify-center font-bold text-lg mb-6">
                    5
                </div>

                <h3 className="font-bebas text-2xl text-[#082F8A] mb-4">
                    Live Scoring & Updates
                </h3>

                <p className="font-inter text-[#1a1a2e]/80 leading-relaxed">
                    Update scores instantly during matches with our powerful
                    live scoring system. Players, coaches, and fans can view
                    real-time results and stats without delays.
                </p>
            </div>

            {/* Step 6 */}
            <div className="bg-white rounded-3xl p-8 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-[#082F8A] text-white flex items-center justify-center font-bold text-lg mb-6">
                    6
                </div>

                <h3 className="font-bebas text-2xl text-[#082F8A] mb-4">
                    View Results & Reports
                </h3>

                <p className="font-inter text-[#1a1a2e]/80 leading-relaxed">
                    Access detailed results, analytics, and reports to evaluate
                    performance and improve future competitions using our
                    tournament and league management software.
                </p>
                </div>
               
            </div>
            </div>
            </section>

            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                <div className="flex justify-center">
                <img
                src={infographic}
                alt="How ScorePal Works"
                className="max-w-5xl w-full h-auto rounded-3xl object-contain"
                            />
                </div>
                </div>
            </section>

            <section className="pt-10 max-md:py-10 bg-[#EDEEF4]">
            <div className="container mx-auto px-6 md:px-12">

                <div className="text-center mb-12 relative z-20">
                    <h2 className="font-bebas text-[clamp(40px,8vw,64px)] leading-tight mb-4 max-md:mb-2 text-[#1F2022] uppercase">
                        Sports Supported by ScorePal
                    </h2>
                    
                    <div className={"flex flex-wrap gap-2 md:text-lg max-sm:text-xl"}>
                        {SPORTS.map((sport) => (<>
                            <a href={sport.href}
                               style={{backgroundImage: `url(${sport.backgroundImage})`}}
                               className={"flex-1 flex items-center bg-primary justify-center rounded-xl min-h-40 text-white bg-cover bg-center min-w-32 shadow-lg"}
                            >{sport.title}</a>
                        </>))}
                        </div>
                    </div>
                </div>
                </section>
                <FaqComponent title={"Faqs About Sports League Software"} faqs={FAQs} />
            <FooterSection />
        </div>
    );
}
